/* ============================================
   ChatWindow — Main chat message area
   Loads messages, realtime subscription,
   date separators, lazy loading, send handlers
   ============================================ */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'
import { showToast } from '@/components/layout/Toast'
import MessageBubble from './MessageBubble'
import MessageInput from './MessageInput'
import type { ScheduleMessageData } from './MessageInput'
import ScheduledBubble from './ScheduledBubble'
import MediaLightbox from './MediaLightbox'
import PdfViewer from './PdfViewer'
import ForwardModal from './ForwardModal'
import ChatSearchBar from './ChatSearchBar'
import ChatMediaPanel from './ChatMediaPanel'
import MediaPreviewModal from './MediaPreviewModal'
import VincularProyectoModal from './VincularProyectoModal'
import NotaBubble from './NotaBubble'
import NotaModal from './NotaModal'
import { useApi } from '@/hooks/useApi'
import { IconMessageSquare } from '@/components/icons/Icons'
import CrossChannelContext from './CrossChannelContext'
import ConversationHeader from './ConversationHeader'
import BotAtendiendo, { DevolverAlBot, useBotDelChat } from './BotAtendiendo'
import type { Mensaje, MensajeProgramado, CanalChat, Conversacion, Contacto, Cliente, Proyecto, ConversacionNota } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface ChatWindowProps {
  conversacionId: number
  contactoId?: number | null
  canal?: CanalChat
  onBack?: () => void
  conversation?: Conversacion | null
  contactMap?: Record<number, Contacto>
  onContactUpdated?: (contacto: Contacto) => void
  /** La conversación cambió (por ahora: se vinculó a un cliente/proyecto). */
  onConversationUpdated?: (conversacion: Conversacion) => void
}

const PAGE_SIZE = 50

/** Format date separator label */
function dateSeparatorLabel(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.floor((today.getTime() - msgDate.getTime()) / 86400000)

  if (diffDays === 0) return 'Hoy'
  if (diffDays === 1) return 'Ayer'
  const dayName = d.toLocaleDateString('es-PE', { weekday: 'long' })
  const capitalDay = dayName.charAt(0).toUpperCase() + dayName.slice(1)
  const dateFormatted = d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  return `${capitalDay} ${dateFormatted}`
}


/** Highlight + center a message that is already rendered in the DOM */
function focusMessage(messageId: number): boolean {
  const el = document.getElementById(`msg-${messageId}`)
  if (!el) return false
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.classList.add('chat-bubble--highlight')
  setTimeout(() => el.classList.remove('chat-bubble--highlight'), 2000)
  return true
}

/**
 * Lo que se pinta en la linea de tiempo: mensajes del canal y notas internas,
 * mezclados y ordenados por fecha. Una nota no es un mensaje (no se envia, no
 * cuenta como no leido, ningun bot la lee), por eso viaja como otra variante.
 */
type ItemChat =
  | { kind: 'mensaje'; fecha: string; msg: Mensaje }
  | { kind: 'nota'; fecha: string; nota: ConversacionNota }

/**
 * Compute grouping info: which items form a consecutive block from the same sender.
 * Una nota SIEMPRE corta el grupo: si no, una nota metida entre dos mensajes del
 * mismo remitente los dejaria pegados por arriba y por abajo, como si no estuviera.
 */
function computeGroupInfo(items: ItemChat[]): Record<number, { start: boolean; end: boolean }> {
  const info: Record<number, { start: boolean; end: boolean }> = {}
  if (items.length === 0) return info

  let groupStart = 0
  for (let i = 1; i < items.length; i++) {
    const prev = items[i - 1]
    const curr = items[i]
    const sameSender = prev.kind === 'mensaje' && curr.kind === 'mensaje'
      && prev.msg.direccion === curr.msg.direccion
    const timeDiff = new Date(curr.fecha).getTime() - new Date(prev.fecha).getTime()
    if (!sameSender || timeDiff > 5 * 60 * 1000) {
      for (let j = groupStart; j < i; j++) {
        info[j] = { start: j === groupStart, end: j === i - 1 }
      }
      groupStart = i
    }
  }
  for (let j = groupStart; j < items.length; j++) {
    info[j] = { start: j === groupStart, end: j === items.length - 1 }
  }
  return info
}

export default function ChatWindow({ conversacionId, contactoId, canal, onBack, conversation, contactMap, onContactUpdated, onConversationUpdated }: ChatWindowProps) {
  const { session, isPartner: esPartner } = useAuth()
  const api = useApi()
  const [messages, setMessages] = useState<Mensaje[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [scheduledMessages, setScheduledMessages] = useState<MensajeProgramado[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const [pdfView, setPdfView] = useState<{ src: string; name: string } | null>(null)
  const [replyingTo, setReplyingTo] = useState<Mensaje | null>(null)
  const [notas, setNotas] = useState<ConversacionNota[]>([])
  const [notaModal, setNotaModal] = useState<{ nota: ConversacionNota | null } | null>(null)

  // Quién atiende este chat ahora mismo. Mientras haya un bot, el cuadro de texto se
  // reemplaza por su barra: dos voces escribiéndole al cliente a la vez es lo peor que
  // puede pasar acá.
  const {
    bot: botDelChat, tomar: tomarElChat, tomando: tomandoElChat,
    devolver: puedeDevolverse, devolverElChat, devolviendo: devolviendoElChat,
    errorDevolver,
  } = useBotDelChat(conversacionId)
  const [forwardMessage, setForwardMessage] = useState<Mensaje | null>(null)
  const [forwardConversations, setForwardConversations] = useState<Conversacion[]>([])
  const [forwardContactMap, setForwardContactMap] = useState<Record<number, Contacto>>({})
  const [scanningTasks, setScanningTasks] = useState(false)
  const [taskCount, setTaskCount] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const loadingMore = useRef(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showMediaPanel, setShowMediaPanel] = useState(false)
  const [showVincularProyecto, setShowVincularProyecto] = useState(false)
  const [clienteVinculado, setClienteVinculado] = useState<Cliente | null>(null)
  const [proyectoVinculado, setProyectoVinculado] = useState<Proyecto | null>(null)

  // Nombres del cliente y proyecto vinculados: la conversación solo guarda los ids.
  useEffect(() => {
    const clienteId = conversation?.cliente_id ?? null
    const proyectoId = conversation?.proyecto_id ?? null
    if (!clienteId) { setClienteVinculado(null); setProyectoVinculado(null); return }

    const token = localStorage.getItem('auth_token')
    const headers = { Authorization: `Bearer ${token}` }
    const base = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

    fetch(`${base}/api/clientes/${clienteId}`, { headers })
      .then(r => (r.ok ? r.json() : null)).then(setClienteVinculado).catch(() => setClienteVinculado(null))

    if (!proyectoId) { setProyectoVinculado(null); return }
    fetch(`${base}/api/proyectos/${proyectoId}`, { headers })
      .then(r => (r.ok ? r.json() : null)).then(setProyectoVinculado).catch(() => setProyectoVinculado(null))
  }, [conversation?.cliente_id, conversation?.proyecto_id])
  const [mediaCount, setMediaCount] = useState(0)

  // Reset typing state when conversation changes
  useEffect(() => {
    setIsTyping(false)
    setShowSearch(false)
    setShowMediaPanel(false)
    // Load task count for this conversation
    loadTaskCount(conversacionId)
    // Load media count
    loadMediaCount(conversacionId)
  }, [conversacionId])

  // Load initial messages
  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setMessages([])
      setHasMore(true)
      prevMsgCount.current = 0
      shouldAutoScroll.current = true

      try {
        const [msgResult, schedResult] = await Promise.all([
          supabase
            .from('mensajes')
            .select('*')
            .eq('conversacion_id', conversacionId)
            .order('fecha_mensaje', { ascending: false })
            .limit(PAGE_SIZE),
          supabase
            .from('mensajes_programados')
            .select('*')
            .eq('conversacion_id', conversacionId)
            .in('estado', ['pendiente', 'enviando', 'fallido'])
            .order('scheduled_at', { ascending: true }),
        ])

        if (msgResult.error) throw msgResult.error
        if (!cancelled) {
          // Reverse so messages display in chronological order (oldest → newest)
          const msgs = ((msgResult.data ?? []) as Mensaje[]).reverse()
          setMessages(msgs)
          setHasMore(msgs.length === PAGE_SIZE)
          setScheduledMessages((schedResult.data ?? []) as MensajeProgramado[])
        }
      } catch {
        if (!cancelled) {
          showToast('Error al cargar mensajes', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [conversacionId])

  // Las notas se piden aparte: son de otra tabla a proposito (ningun bot las lee).
  const recargarNotas = useCallback(async () => {
    try {
      setNotas(await api.get<ConversacionNota[]>(`/api/chat/conversaciones/${conversacionId}/notas`))
    } catch { /* una nota que no carga no puede tumbar el chat */ }
  }, [api, conversacionId])

  // Depende del chat, NO de `recargarNotas`: una dependencia de identidad en un efecto
  // que además hace setState es un bucle esperando a pasar, y pasó.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNotas([]); recargarNotas() }, [conversacionId])

  const handleBorrarNota = useCallback(async (nota: ConversacionNota) => {
    if (!confirm('Se elimina la nota y sus adjuntos. No se puede deshacer.')) return
    try {
      await api.del(`/api/chat/notas/${nota.id}`)
      setNotas((prev) => prev.filter((n) => n.id !== nota.id))
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo eliminar la nota', 'error')
    }
  }, [api])

  // Track whether we should auto-scroll to bottom
  const shouldAutoScroll = useRef(true)
  const prevMsgCount = useRef(0)

  // When messages change, decide if we should auto-scroll
  useEffect(() => {
    if (!loading && scrollRef.current) {
      const el = scrollRef.current
      const isNewConversation = prevMsgCount.current === 0
      const isNewMessage = messages.length > prevMsgCount.current

      if (isNewConversation) {
        // Always scroll on initial load
        shouldAutoScroll.current = true
      } else if (isNewMessage) {
        // Only auto-scroll if user is near the bottom (within 150px)
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
        shouldAutoScroll.current = distanceFromBottom < 150
      }

      prevMsgCount.current = messages.length

      if (shouldAutoScroll.current) {
        // Double rAF ensures scroll happens after DOM paint completes
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight
            }
          })
        })
      }
    }
  }, [loading, messages.length])

  // ResizeObserver: re-scroll when content height changes (e.g., images loading)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || loading) return

    const observer = new ResizeObserver(() => {
      if (shouldAutoScroll.current && el) {
        el.scrollTop = el.scrollHeight
      }
    })

    // Observe the scroll container's inner content
    for (const child of el.children) {
      observer.observe(child)
    }

    return () => observer.disconnect()
  }, [loading, messages.length])

  // Realtime SSE: new messages for this conversation
  useRealtimeEvent('new_message', useCallback((data: any) => {
    const conversacionIdFromEvent = data?.conversacion_id
    if (conversacionIdFromEvent !== conversacionId) return

    const msg = data?.mensaje as Mensaje | undefined
    if (!msg) return

    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev
      return [...prev, msg]
    })
  }, [conversacionId]))

  // Realtime SSE: message updates (status changes)
  useRealtimeEvent('message_updated', useCallback((data: any) => {
    const conversacionIdFromEvent = data?.conversacion_id
    if (conversacionIdFromEvent !== conversacionId) return

    const msg = data?.mensaje as Mensaje | undefined
    if (!msg) return

    setMessages((prev) =>
      prev.map((m) => m.id === msg.id ? { ...m, ...msg } : m)
    )
  }, [conversacionId]))

  // Realtime SSE: typing indicator (contact is typing)
  useRealtimeEvent('contact_typing', useCallback((data: any) => {
    if (data?.conversacion_id !== conversacionId) return
    setIsTyping(true)
    // Auto-clear after 5s if no further typing events arrive
    clearTimeout((window as any).__typingTimer)
    ;(window as any).__typingTimer = setTimeout(() => setIsTyping(false), 5000)
  }, [conversacionId]))

  // Clear typing indicator when a new message arrives from the contact
  useEffect(() => {
    if (messages.length > 0) {
      const last = messages[messages.length - 1]
      if (last.direccion === 'entrante') {
        setIsTyping(false)
      }
    }
  }, [messages.length])

  // Lazy load older messages on scroll to top
  const handleScroll = useCallback(async () => {
    const el = scrollRef.current
    if (!el) return

    // Always track scroll position so ResizeObserver respects user intent
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    shouldAutoScroll.current = distanceFromBottom < 150

    if (loadingMore.current || !hasMore) return

    if (el.scrollTop < 100) {
      loadingMore.current = true
      const oldestMsg = messages[0]
      if (!oldestMsg) {
        loadingMore.current = false
        return
      }

      try {
        const { data, error } = await supabase
          .from('mensajes')
          .select('*')
          .eq('conversacion_id', conversacionId)
          .lt('fecha_mensaje', oldestMsg.fecha_mensaje)
          .order('fecha_mensaje', { ascending: false })
          .limit(PAGE_SIZE)

        if (error) throw error

        const older = ((data ?? []) as Mensaje[]).reverse()
        if (older.length === 0) {
          setHasMore(false)
        } else {
          // Preserve scroll position
          const prevHeight = el.scrollHeight
          setMessages((prev) => [...older, ...prev])
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight - prevHeight
          })
          setHasMore(older.length === PAGE_SIZE)
        }
      } catch {
        showToast('Error al cargar mensajes anteriores', 'error')
      } finally {
        loadingMore.current = false
      }
    }
  }, [conversacionId, hasMore, messages])

  // Schedule a message for future sending
  const handleScheduleMessage = async (data: ScheduleMessageData) => {
    if (!session?.access_token) {
      showToast('Sesion no encontrada', 'error')
      return
    }

    setSending(true)
    try {
      let mediaData: Record<string, unknown> = {}

      // If file, upload to R2 first
      if (data.file) {
        const formData = new FormData()
        formData.append('file', data.file)
        formData.append('conversacion_id', String(conversacionId))

        const res = await fetch(
          `${API_URL}/api/chat/upload-media`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: formData,
          }
        )

        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error((body as Record<string, string>).error || 'Error al subir archivo')
        }

        const uploadResult = await res.json() as {
          url: string; tipo: string; nombre: string; mime: string; size: number
        }
        mediaData = {
          media_url: uploadResult.url,
          media_mime: uploadResult.mime,
          media_nombre: uploadResult.nombre,
          media_size: uploadResult.size,
          tipo: uploadResult.tipo,
        }
      }

      // Call schedule edge function
      const { error } = await supabase.functions.invoke('chat-schedule-message', {
        body: {
          conversacion_id: conversacionId,
          tipo: data.tipo,
          contenido: data.contenido ?? null,
          fecha_programada: data.scheduled_at,
          ...mediaData,
        },
      })
      if (error) throw error

      showToast('Mensaje programado exitosamente', 'success')
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Error al programar mensaje',
        'error'
      )
    } finally {
      setSending(false)
    }
  }

  // Cancel a scheduled message
  const handleCancelScheduled = async (id: number) => {
    try {
      const { error } = await supabase.functions.invoke('chat-cancel-scheduled', {
        body: { id },
      })
      if (error) throw error
      setScheduledMessages((prev) => prev.filter((m) => m.id !== id))
      showToast('Mensaje cancelado', 'info')
    } catch {
      showToast('Error al cancelar', 'error')
    }
  }

  // Send text message
  const handleSendText = async (text: string) => {
    setSending(true)
    try {
      const { data, error } = await supabase.functions.invoke('chat-send', {
        body: {
          conversacion_id: conversacionId,
          tipo: 'texto',
          contenido: text,
          ...(replyingTo ? { reply_to_id: replyingTo.id } : {}),
        },
      })
      if (error) throw error
      if (data) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev
          return [...prev, data as Mensaje]
        })
      }
      setReplyingTo(null)
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Error al enviar mensaje',
        'error'
      )
    } finally {
      setSending(false)
    }
  }

  // Send media file (with optional caption)
  const handleSendMedia = async (file: File, caption?: string) => {
    if (!session?.access_token) {
      showToast('Sesion no encontrada', 'error')
      return
    }

    setSending(true)
    try {
      // Step 1: Upload file to R2
      const formData = new FormData()
      formData.append('file', file)
      formData.append('conversacion_id', String(conversacionId))

      const res = await fetch(
        `${API_URL}/api/chat/upload-media`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      )

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as Record<string, string>).error || 'Error al subir archivo')
      }

      const uploadResult = await res.json() as {
        url: string; tipo: string; nombre: string; mime: string; size: number
      }

      // Step 2: Create message and send via WhatsApp
      // Use caption if provided, otherwise fall back to filename for non-images
      const contenido = caption || (uploadResult.tipo === 'imagen' ? undefined : uploadResult.nombre)

      const { data: sendData, error: sendError } = await supabase.functions.invoke('chat-send', {
        body: {
          conversacion_id: conversacionId,
          tipo: uploadResult.tipo,
          contenido,
          media_url: uploadResult.url,
          media_nombre: uploadResult.nombre,
          media_mime: uploadResult.mime,
          media_size: uploadResult.size,
          ...(replyingTo ? { reply_to_id: replyingTo.id } : {}),
        },
      })
      if (sendError) throw sendError
      if (sendData) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === sendData.id)) return prev
          return [...prev, sendData as Mensaje]
        })
      }
      setReplyingTo(null)
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Error al enviar archivo',
        'error'
      )
    } finally {
      setSending(false)
    }
  }

  // Callback for MediaPreviewModal: send file with caption, then close modal
  const handlePreviewSend = useCallback((file: File, caption?: string) => {
    handleSendMedia(file, caption)
    setPendingFile(null)
  }, [handleSendMedia])

  // Send audio recording
  const handleSendAudio = async (blob: Blob, duration: number) => {
    if (!session?.access_token) {
      showToast('Sesion no encontrada', 'error')
      return
    }

    setSending(true)
    try {
      const file = new File([blob], `audio_${Date.now()}.webm`, { type: blob.type })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('conversacion_id', String(conversacionId))
      formData.append('duration', String(duration))
      formData.append('tipo', 'audio')

      const res = await fetch(
        `${API_URL}/api/chat/upload-media`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      )

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as Record<string, string>).error || 'Error al enviar audio')
      }

      const uploadResult = await res.json() as {
        url: string; tipo: string; nombre: string; mime: string; size: number
      }

      const { data: sendData, error: sendError } = await supabase.functions.invoke('chat-send', {
        body: {
          conversacion_id: conversacionId,
          tipo: 'audio',
          media_url: uploadResult.url,
          media_nombre: uploadResult.nombre,
          media_mime: uploadResult.mime,
          media_size: uploadResult.size,
          media_duracion: duration,
          ...(replyingTo ? { reply_to_id: replyingTo.id } : {}),
        },
      })
      if (sendError) throw sendError
      if (sendData) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === sendData.id)) return prev
          return [...prev, sendData as Mensaje]
        })
      }
      setReplyingTo(null)
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Error al enviar audio',
        'error'
      )
    } finally {
      setSending(false)
    }
  }

  // Forward a message — open modal to select destination conversations
  const handleForward = useCallback(async (message: Mensaje) => {
    setForwardMessage(message)
    try {
      const { data: convs } = await supabase.from('conversaciones').select('*')
      if (convs) {
        const sorted = (convs as Conversacion[]).filter(c => c.id !== conversacionId)
        setForwardConversations(sorted)
        const contactIds = [...new Set(sorted.filter(c => c.contacto_id).map(c => c.contacto_id!))]
        if (contactIds.length > 0) {
          const { data: contacts } = await supabase.from('contactos').select('*').in('id', contactIds)
          if (contacts) {
            const map: Record<number, Contacto> = {}
            for (const c of contacts) map[(c as Contacto).id] = c as Contacto
            setForwardContactMap(map)
          }
        }
      }
    } catch { /* non-critical */ }
  }, [conversacionId])

  const handleForwardSend = useCallback(async (targetIds: number[]) => {
    if (!forwardMessage) return
    const content = forwardMessage.contenido || ''
    for (const targetId of targetIds) {
      try {
        await supabase.functions.invoke('chat-send', {
          body: { conversacion_id: targetId, tipo: 'texto', contenido: content },
        })
      } catch { /* continue */ }
    }
    showToast(`Mensaje reenviado a ${targetIds.length} conversacion(es)`, 'success')
    setForwardMessage(null)
    setForwardConversations([])
  }, [forwardMessage])

  /*
    Mensajes y notas en una sola lista ordenada por fecha. Los mensajes vienen
    PAGINADOS, asi que una nota anterior al tramo cargado se omite: si no, quedaria
    flotando arriba de todo, fuera de contexto. Reaparece al cargar mensajes mas
    viejos. Sin mensajes cargados todavia, se muestran todas.
  */
  const items = useMemo<ItemChat[]>(() => {
    const masViejoCargado = messages.length
      ? new Date(messages[0].fecha_mensaje).getTime()
      : null

    const lista: ItemChat[] = messages.map((msg) => ({
      kind: 'mensaje', fecha: msg.fecha_mensaje, msg,
    }))

    for (const nota of notas) {
      if (masViejoCargado !== null && new Date(nota.fecha).getTime() < masViejoCargado) continue
      lista.push({ kind: 'nota', fecha: nota.fecha, nota })
    }

    return lista.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
  }, [messages, notas])

  const groupInfo = computeGroupInfo(items)

  /** Scroll to a specific message (used when clicking reply previews or search results).
   *  If the message is not in the DOM (paginated out), load older messages until found. */
  const scrollToMessage = useCallback(async (messageId: number) => {
    // First, try to find in the current DOM
    const el = document.getElementById(`msg-${messageId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('chat-bubble--highlight')
      setTimeout(() => el.classList.remove('chat-bubble--highlight'), 2000)
      return
    }

    // Message not in DOM — try to load older messages until we find it
    if (!scrollRef.current) return

    // Check if message exists in our already-loaded array
    const alreadyLoaded = messages.some(m => m.id === messageId)
    if (alreadyLoaded) {
      // It's in state but DOM hasn't rendered yet — wait a frame and retry
      requestAnimationFrame(() => {
        const retryEl = document.getElementById(`msg-${messageId}`)
        if (retryEl) {
          retryEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          retryEl.classList.add('chat-bubble--highlight')
          setTimeout(() => retryEl.classList.remove('chat-bubble--highlight'), 2000)
        }
      })
      return
    }

    // Need to load older messages to find the target
    try {
      // Load older messages from current oldest
      const oldestMsg = messages[0]
      if (!oldestMsg) return

      const { data, error } = await supabase
        .from('mensajes')
        .select('*')
        .eq('conversacion_id', conversacionId)
        .lt('fecha_mensaje', oldestMsg.fecha_mensaje)
        .order('fecha_mensaje', { ascending: false })
        .limit(500) // Load more messages to increase chance of finding target

      if (error) throw error

      const older = ((data ?? []) as Mensaje[]).reverse()
      if (older.length === 0) return

      const scrEl = scrollRef.current
      const prevHeight = scrEl.scrollHeight
      setMessages(prev => [...older, ...prev])
      setHasMore(older.length === 500)

      // Wait for DOM update, then scroll to the target
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Preserve scroll position first
          if (scrEl) {
            scrEl.scrollTop = scrEl.scrollHeight - prevHeight
          }

          // Now find and scroll to the target message
          const targetEl = document.getElementById(`msg-${messageId}`)
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            targetEl.classList.add('chat-bubble--highlight')
            setTimeout(() => targetEl.classList.remove('chat-bubble--highlight'), 2000)
          }
        })
      })
    } catch {
      // Silently fail — message might be too old or deleted
    }
  }, [messages, conversacionId])

  /** Jump to the first message on (or after) a calendar date (YYYY-MM-DD).
   *  Loads older pages backwards until the target enters the window, so el
   *  historial cargado sigue siendo un rango continuo hasta el mensaje mas nuevo. */
  const jumpToDate = useCallback(async (isoDate: string) => {
    if (!isoDate) return
    const dayStart = new Date(`${isoDate}T00:00:00`)
    if (isNaN(dayStart.getTime())) return

    try {
      // 1. Primer mensaje en esa fecha o despues
      const { data: found, error: findErr } = await supabase
        .from('mensajes')
        .select('*')
        .eq('conversacion_id', conversacionId)
        .gte('fecha_mensaje', dayStart.toISOString())
        .order('fecha_mensaje', { ascending: true })
        .limit(1)

      if (findErr) throw findErr
      const target = ((found ?? []) as Mensaje[])[0]
      if (!target) {
        showToast('No hay mensajes en esa fecha ni despues', 'info')
        return
      }

      if (new Date(target.fecha_mensaje).toDateString() !== dayStart.toDateString()) {
        showToast(`Sin mensajes ese dia — te llevo al ${dateSeparatorLabel(target.fecha_mensaje)}`, 'info')
      }

      // 2. Ya esta cargado: solo desplazar
      if (messages.some((m) => m.id === target.id)) {
        focusMessage(target.id)
        return
      }

      // 3. Traer paginas hacia atras hasta alcanzar el objetivo
      const el = scrollRef.current
      let oldest = messages[0]
      if (!el || !oldest) return

      const collected: Mensaje[] = []
      for (let i = 0; i < 20; i++) {
        const { data, error } = await supabase
          .from('mensajes')
          .select('*')
          .eq('conversacion_id', conversacionId)
          .lt('fecha_mensaje', oldest.fecha_mensaje)
          .gte('fecha_mensaje', target.fecha_mensaje)
          .order('fecha_mensaje', { ascending: false })
          .limit(500)

        if (error) throw error
        const page = ((data ?? []) as Mensaje[]).reverse()
        if (page.length === 0) break
        collected.unshift(...page)
        oldest = page[0]
        if (page.some((m) => m.id === target.id)) break
      }

      if (collected.length === 0) return

      const prevHeight = el.scrollHeight
      shouldAutoScroll.current = false
      setMessages((prev) => [...collected, ...prev])

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - prevHeight
          focusMessage(target.id)
        })
      })
    } catch {
      showToast('Error al saltar a esa fecha', 'error')
    }
  }, [conversacionId, messages])

  // ─── Drag & Drop + File preview ───
  const [dragOver, setDragOver] = useState(false)
  const dragCounter = useRef(0)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  // ─── Backlog: Task Panel + AI Scanning ───
  const API_URL_BASE = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'
  const [showTaskPanel, setShowTaskPanel] = useState(false)
  const [convTasks, setConvTasks] = useState<any[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)

  async function loadConvTasks(convId: number) {
    if (!session?.access_token) return
    setLoadingTasks(true)
    try {
      const res = await fetch(`${API_URL_BASE}/api/backlog/by-conversacion/${convId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (res.ok) {
        const tasks = await res.json()
        const arr = Array.isArray(tasks) ? tasks : []
        setConvTasks(arr)
        setTaskCount(arr.length)
      }
    } catch { /* silent */ }
    finally { setLoadingTasks(false) }
  }

  async function loadTaskCount(convId: number) {
    if (!session?.access_token) return
    try {
      const res = await fetch(`${API_URL_BASE}/api/backlog/by-conversacion/${convId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (res.ok) {
        const tasks = await res.json()
        setTaskCount(Array.isArray(tasks) ? tasks.length : 0)
      }
    } catch { /* silent */ }
  }

  async function loadMediaCount(convId: number) {
    if (!session?.access_token) return
    try {
      const res = await fetch(`${API_URL_BASE}/api/chat/mensajes/media?conversacion_id=${convId}&limit=1&offset=0`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setMediaCount(data.total || 0)
      }
    } catch { /* silent */ }
  }

  function handleToggleTaskPanel() {
    if (!showTaskPanel) {
      loadConvTasks(conversacionId)
    }
    setShowTaskPanel(!showTaskPanel)
  }

  async function handleScanTasks() {
    if (!session?.access_token || scanningTasks) return
    setScanningTasks(true)
    try {
      const res = await fetch(`${API_URL_BASE}/api/backlog/scan/${conversacionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (res.ok) {
        const result = await res.json()
        const count = result.tareas_creadas || 0
        if (count > 0) {
          showToast(`🤖 ${count} tarea${count > 1 ? 's' : ''} detectada${count > 1 ? 's' : ''}`, 'success')
        } else {
          showToast('No se detectaron tareas nuevas', 'info')
        }
        // Refresh task list
        loadConvTasks(conversacionId)
      } else {
        showToast('Error al escanear conversación', 'error')
      }
    } catch {
      showToast('Error al escanear conversación', 'error')
    } finally {
      setScanningTasks(false)
    }
  }

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) {
      setDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setDragOver(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      if (file.size > 16 * 1024 * 1024) {
        showToast('El archivo excede el limite de 16 MB', 'error')
        return
      }
      setPendingFile(file)
    }
  }, [])

  // ─── Clipboard paste (Ctrl+V) ───
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) {
            setPendingFile(file)
          }
          return
        }
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [])

  if (loading) {
    return (
      <div className="chat-window">
        <div className="chat-window__empty">
          <div className="auth-spinner" style={{ width: 24, height: 24 }} />
          <span>Cargando mensajes...</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className="chat-window"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Conversation Header */}
      {conversation && (
        <ConversationHeader
          conversation={conversation}
          contacto={contactoId && contactMap ? (contactMap[contactoId] ?? null) : null}
          onClose={onBack || (() => {})}
          onCreateContact={() => {}}
          onLinkContact={() => {}}
          isTyping={isTyping}
          onScanTasks={handleToggleTaskPanel}
          scanningTasks={scanningTasks}
          taskCount={taskCount}
          onContactUpdated={onContactUpdated}
          onToggleSearch={() => { setShowSearch(prev => !prev); setShowMediaPanel(false) }}
          onToggleMedia={() => { setShowMediaPanel(prev => !prev); setShowSearch(false) }}
          mediaCount={mediaCount}
          onVincularProyecto={() => setShowVincularProyecto(true)}
          onNuevaNota={() => setNotaModal({ nota: null })}
          nombreCliente={clienteVinculado?.empresa ?? null}
          nombreProyecto={proyectoVinculado?.nombre ?? null}
        />
      )}

      {/* Search Bar */}
      {showSearch && (
        <ChatSearchBar
          conversacionId={conversacionId}
          contactMap={contactMap}
          onScrollToMessage={scrollToMessage}
          onJumpToDate={jumpToDate}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Media Panel */}
      {showMediaPanel && (
        <ChatMediaPanel
          conversacionId={conversacionId}
          messages={messages}
          onImageClick={(url) => setLightboxSrc(url)}
          onPdfClick={(url, name) => setPdfView({ src: url, name })}
          onClose={() => setShowMediaPanel(false)}
        />
      )}

      {/* Task Panel (slides from right) */}
      {showTaskPanel && (
        <div className="chat-task-panel">
          <div className="chat-task-panel__header">
            <h3>Tareas de esta conversación</h3>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleScanTasks}
                disabled={scanningTasks}
                type="button"
              >
                {scanningTasks ? (
                  <><div className="auth-spinner" style={{ width: 12, height: 12 }} /> Analizando...</>
                ) : (
                  '🤖 Escanear con Noa'
                )}
              </button>
              <button
                className="chat-task-panel__close"
                onClick={() => setShowTaskPanel(false)}
                type="button"
              >✕</button>
            </div>
          </div>

          <div className="chat-task-panel__body">
            {loadingTasks ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                <div className="auth-spinner" style={{ width: 20, height: 20, margin: '0 auto 8px' }} />
                Cargando tareas...
              </div>
            ) : convTasks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
                <p>No hay tareas detectadas aún</p>
                <p style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  Haz clic en <strong>"Escanear con Noa"</strong> para que la IA analice la conversación
                </p>
              </div>
            ) : (
              convTasks.map((task: any) => (
                <div key={task.id} className="chat-task-panel__card">
                  <div className="chat-task-panel__card-header">
                    <span className={`backlog-priority backlog-priority--${task.prioridad || 'media'}`}>
                      {task.prioridad || 'media'}
                    </span>
                    <span className={`backlog-status backlog-status--${task.estado || 'pendiente'}`}>
                      {task.estado || 'pendiente'}
                    </span>
                    {task.ai_confidence != null && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                        {Math.round(task.ai_confidence * 100)}% conf.
                      </span>
                    )}
                  </div>
                  <div className="chat-task-panel__card-title">{task.titulo}</div>
                  {task.descripcion && (
                    <div className="chat-task-panel__card-desc">{task.descripcion}</div>
                  )}
                  {task.followup_sugerido && (
                    <div className="chat-task-panel__card-followup">
                      💡 {task.followup_sugerido}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {/* Drop zone overlay */}
      {dragOver && (
        <div className="chat-drop-overlay">
          <div className="chat-drop-overlay__content">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span>Suelta el archivo aquí</span>
          </div>
        </div>
      )}
      {/* Messages scroll area */}
      <div
        className="chat-messages"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {/* Cross-channel context card */}
        {contactoId && canal && (
          <CrossChannelContext
            contactoId={contactoId}
            currentConversationId={conversacionId}
            currentCanal={canal}
          />
        )}

        {/* Load more indicator */}
        {hasMore && messages.length > 0 && (
          <div className="chat-messages__load-hint">
            <span>Desplaza arriba para cargar mas</span>
          </div>
        )}

        {/* Empty state */}
        {messages.length === 0 && (
          <div className="chat-window__empty">
            <IconMessageSquare size={40} />
            <span>Sin mensajes en esta conversacion</span>
          </div>
        )}

        {/* Mensajes y notas con separadores de dia */}
        {(() => {
          const dayGroups: { dateStr: string; items: { item: ItemChat; idx: number }[] }[] = []
          let lastDate = ''

          items.forEach((item, idx) => {
            const itemDate = new Date(item.fecha).toDateString()
            if (itemDate !== lastDate) {
              dayGroups.push({ dateStr: item.fecha, items: [] })
              lastDate = itemDate
            }
            dayGroups[dayGroups.length - 1].items.push({ item, idx })
          })

          return dayGroups.map((group, gIdx) => {
            return (
              <div className="chat-messages__day-group" key={`group-${gIdx}`}>
                <div className="chat-messages__date-sep">
                  <span>{dateSeparatorLabel(group.dateStr)}</span>
                </div>
                {group.items.map(({ item, idx }) => {
                  if (item.kind === 'nota') {
                    return (
                      <NotaBubble
                        key={`nota-${item.nota.id}`}
                        nota={item.nota}
                        onEdit={(nota) => setNotaModal({ nota })}
                        onDelete={handleBorrarNota}
                        onImageClick={(url) => setLightboxSrc(url)}
                      />
                    )
                  }
                  const g = groupInfo[idx]
                  return (
                    <MessageBubble
                      key={item.msg.id}
                      messageId={item.msg.id}
                      message={item.msg}
                      isGroupStart={g?.start ?? true}
                      isGroupEnd={g?.end ?? true}
                      onImageClick={(url) => setLightboxSrc(url)}
                      onPdfClick={(url, name) => setPdfView({ src: url, name })}
                      onReply={setReplyingTo}
                      onForward={handleForward}
                      onScrollToMessage={scrollToMessage}
                    />
                  )
                })}
              </div>
            )
          })
        })()}

        {/* Scheduled messages section */}
        {scheduledMessages.length > 0 && (
          <>
            <div className="chat-messages__date-sep">
              <span>🕐 Programados</span>
            </div>
            {scheduledMessages.map((sm) => (
              <ScheduledBubble
                key={sm.id}
                programado={sm}
                onCancel={handleCancelScheduled}
                onImageClick={(url) => setLightboxSrc(url)}
                onPdfClick={(url, name) => setPdfView({ src: url, name })}
              />
            ))}
          </>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input — o la barra del bot, que lo reemplaza mientras el bot atiende.
          Se reemplaza y no se deshabilita: un cuadro de texto gris no dice por qué. */}
      {/* Los bots (recepcionista, soporte, pedidos, estado) son NUESTROS y
          corren sobre nuestra cuenta de IA: el partner no tiene ninguno, así
          que la barra no le aplica y escribe directo. */}
      {botDelChat && !esPartner ? (
        <BotAtendiendo bot={botDelChat} onTomar={tomarElChat} tomando={tomandoElChat} />
      ) : (
        <>
          {/* Si el recepcionista quedó parado acá, la forma de devolvérselo. No bloquea:
              el chat es de quien lo tomó. */}
          {puedeDevolverse && (
            <DevolverAlBot
              devolver={puedeDevolverse}
              onDevolver={devolverElChat}
              devolviendo={devolviendoElChat}
              error={errorDevolver}
            />
          )}
          <MessageInput
            onSendText={handleSendText}
            onSendMedia={handleSendMedia}
            onSendAudio={handleSendAudio}
            onScheduleMessage={handleScheduleMessage}
            onFileSelected={(file) => setPendingFile(file)}
            sending={sending}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
          />
        </>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <MediaLightbox
          src={lightboxSrc}
          onClose={() => setLightboxSrc(null)}
        />
      )}

      {/* PDF Viewer */}
      {pdfView && (
        <PdfViewer
          src={pdfView.src}
          name={pdfView.name}
          onClose={() => setPdfView(null)}
        />
      )}

      {/* Forward Modal */}
      {forwardMessage && (
        <ForwardModal
          conversations={forwardConversations}
          contactMap={forwardContactMap}
          messagePreview={forwardMessage.contenido?.slice(0, 80) || forwardMessage.tipo}
          onForward={handleForwardSend}
          onClose={() => { setForwardMessage(null); setForwardConversations([]) }}
        />
      )}

      {/* Vincular chat a cliente/proyecto */}
      {showVincularProyecto && conversation && (
        <VincularProyectoModal
          conversacion={conversation}
          onSave={(actualizada) => {
            setShowVincularProyecto(false)
            onConversationUpdated?.(actualizada)
          }}
          onClose={() => setShowVincularProyecto(false)}
        />
      )}

      {/* Nota interna: crear o editar */}
      {notaModal && (
        <NotaModal
          open
          conversacionId={conversacionId}
          nota={notaModal.nota}
          onClose={() => setNotaModal(null)}
          onSaved={recargarNotas}
        />
      )}

      {/* Media Preview Modal (for drag&drop, paste, and manual selection) */}
      {pendingFile && (
        <MediaPreviewModal
          file={pendingFile}
          onSend={handlePreviewSend}
          onCancel={() => setPendingFile(null)}
          sending={sending}
        />
      )}
    </div>
  )
}
